import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import { STATIC_DATA_CONFIG } from '@/lib/queryClient';
import type { Cuisine } from '@/lib/types';

/**
 * Optimized hook for fetching cuisines with aggressive caching
 * - 24h staleTime ensures instant load on subsequent visits
 * - 7d gcTime keeps data in memory even when component unmounts
 * - No refetch on mount/focus/reconnect - treated as static data
 * - keepPreviousData ensures instant UI render while revalidating
 */
export function useCuisines() {
  return useQuery<Cuisine[]>({
    queryKey: queryKeys.cuisines.list(),
    queryFn: async () => {
      const response = await fetch('/api/cuisines', { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Failed to fetch cuisines');
      }
      return response.json();
    },
    ...STATIC_DATA_CONFIG,
    // Keep previous data for instant render during background revalidation
    placeholderData: (previousData) => previousData,
  });
}
